import type { ClientItem } from '../dto/searchResultHit';
import type { Ingest } from '../dto/itemPropTypes';
import { ItemState } from '../dto/itemState';
import type { SearchItemType } from '../dto/itemTypes';
import { ItemType } from '../dto/itemTypes';
import { SEGMENTED_HIGHRES_TS_STREAM_ID } from '../dto/mediaDtos';
import isObject from 'lodash/isObject';
import type { RequiredBy } from '../util/general';
import type { ExternalProxyDto } from '../dto/externalProxyDto';
import type { ProxyFileLocation } from '../dto/fileLocation';
import { StreamRoleId } from '../dto/mediaAvailability';
export const IMAGE_ONLY = '.jpg,.png,.avif';
export const VIDEO_ONLY = 'video/mp4,.mp4,video/x-m4v,.m4v,video/*,.mxf,.mov';
export const AUDIO_ONLY = 'audio/mp3,.mp3,audio/wav,.wav,audio/m4a,.m4a,audio/aac,.aac,audio/ogg,.ogg,audio/flac,.flac';

/**
 * Checks whether the media is online or offline based on Item Payload
 *
 * This function is used on database entity Item objects, under the assumption
 * that the model Item and database entity Item share the same definition of the
 * 'archiveInfo' property.
 *
 * TODO: Express this assumption explicitly in the type system.
 *
 * @param item - Item payload
 */
export const isMediaOnline = (item: Pick<ClientItem, 'archiveInfo'>) => {
  if (
    item &&
    (!item.archiveInfo ||
      (item.archiveInfo.mediaOnlineUntil &&
        new Date(item.archiveInfo.mediaOnlineUntil) > new Date()))
  ) {
    return true;
  }
  return false;
};


/**
 * This function determines whether an item has media that _is/was/will be_ streamed to it.
 * This logic has been used in various places, including when it needs to be determined whether
 * MediaSegmentsDownloader instead of the regular downloader has to be used to download/import this item.
 */
export const isStreaming = (item: Pick<ClientItem, 'media' | 'itemState' | 'liveRecording'> | null): boolean => {
  if (!item) {
    return false;
  }
  if (item.liveRecording !== undefined) {
    return true;
  }
  const hasMediaSegments_ = item.media !== undefined;
  const itemState = item.itemState;
  const streaming = item?.media?.streaming === true;
  const notStreamingButCouldStartAnyTime = hasMediaSegments_ && (itemState !== ItemState.COMPLETE && itemState !== ItemState.ERROR);
  return streaming || notStreamingButCouldStartAnyTime;
};


export function isImage(contentType: string): boolean {
  if (contentType) {
    const regex = new RegExp('image/*');
    return regex.test(contentType.toLowerCase());
  }
  return false;
}

export function itemSupportsVirtualClip(item: ClientItem): boolean {
  if (item.media === undefined) {
    return true;
  }
  return (item.media.codecsByStreamId[StreamRoleId.LiveRecordHighresTs] === undefined);
}

export function hasUnsegmentedBrowsePreviewVideo(item: ClientItem): boolean {
  return item.itemType == ItemType.VIDEO && item.proxy != null;
}


export const hasLiveRecordingInfo = (item: Pick<ClientItem, 'liveRecording'> | null): boolean => {
  return !!item?.liveRecording;
};

export const isCurrentlyLive = (item: Pick<ClientItem, 'media' | 'isLive'> | null): boolean => {
  if (item?.isLive) {
    // Search results may not have the media object on them, but
    // instead have a isLive boolean
    return true;
  }
  return item?.media?.streaming ?? false;
};

export const showStreamingIndicator = (item: Pick<ClientItem, 'media' | 'isLive'> | null | undefined): boolean => {
  if (!item) {
    return false;
  }
  return isCurrentlyLive(item);
};


export const hasMediaSegments = (item: Pick<ClientItem, 'media'> | null): boolean => item?.media !== undefined;

export const highresReferenceProperties = <const>['ingest', 'ingestSourceFullPath', 'archiveLocationFullPath'];
export const doesItemHaveFlatHighres = (
  item: {
    ingest?: Ingest,
    ingestSourceFullPath?: string,
    archiveLocationFullPath?: string,
  },
) => {
  return Boolean(
    highresReferenceProperties.find(prop => Boolean(item[prop]))
  );
};

export function canSendItemToStorage(
  item: Pick<ClientItem, 'media' | 'isLive' | 'highRes' | 'ingest' | 'ingestSourceFullPath' | 'aaf'> & {archiveLocationFullPath?: string},
) {
  return !isCurrentlyLive(item) && (Boolean(item.highRes) || doesItemHaveFlatHighres(item) || doesItemHaveHighresTsSegments(item) || Boolean(item.aaf));
}

export function doesItemHaveHighresTsSegments(item: Pick<ClientItem, 'media'>): boolean {
  return Boolean(item.media?.codecsByStreamId[SEGMENTED_HIGHRES_TS_STREAM_ID]);
}

export function doesItemHaveHighres(item: Pick<ClientItem, 'ingest' | 'ingestSourceFullPath' | 'media'>): boolean {
  return doesItemHaveFlatHighres(item) || doesItemHaveHighresTsSegments(item);
}

export const getProxyFileNameAndMimeType = (itemType: SearchItemType | undefined) => {
  let proxyFileName = 'proxy';
  let proxyMimeType = 'application/octet-stream';

  if (itemType === ItemType.IMAGE) {
    proxyFileName = 'proxy.png';
    proxyMimeType = 'image/png';
  } else if (itemType === ItemType.VIDEO) {
    proxyFileName = 'proxy_1.mp4';
    proxyMimeType = 'video/mp4';
  } else if (itemType === ItemType.AUDIO) {
    proxyFileName = 'proxy.m4a';
    proxyMimeType = 'audio/m4a';
  }
  return { fileName: proxyFileName, mimeType: proxyMimeType };
};

export const isHighresDeletionEligibleItem = (obj: unknown): obj is ClientItem => {
  if (!isObject(obj)) return false;
  const probablyItem = obj as Partial<ClientItem>;
  return (doesItemHaveFlatHighres(probablyItem) || doesItemHaveHighresTsSegments(probablyItem));
};

export function isHighresInArchiveBucket<ItemObj extends { archiveS3Bucket?: string, archiveLocationFullPath?: string }>(item: ItemObj): item is RequiredBy<ItemObj, 'archiveS3Bucket' | 'archiveLocationFullPath'> {
  return Boolean(item.archiveS3Bucket && item.archiveLocationFullPath);
}

export function isExternalProxyOnlyItem<ItemObj extends { externalProxyInfo?: ExternalProxyDto, webProxyLocation?: ProxyFileLocation }>(item: ItemObj): item is RequiredBy<ItemObj, 'externalProxyInfo'> {
  return Boolean(item.externalProxyInfo && !item.webProxyLocation);
}

export function isSegmentedKeldaProxy(item: {
  liveRecording?: ClientItem['liveRecording'],
  ingest?: ClientItem['ingest'],
  keldaIngest?: ClientItem['keldaIngest'],
  media?: { expectedStreams?: StreamRoleId[] },
}): boolean {
  return Boolean(
    item.liveRecording === undefined
    && (item.ingest?.source?.scheme === 'kelda' || item.keldaIngest)
    && item.media?.expectedStreams?.includes(StreamRoleId.LiveRecordWebproxyTs)
  );
}
